import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { EmergencySheet } from '@gk/contracts'
import { useClub } from '../../club/ClubProvider.tsx'
import { sheetRows, telHref, useJobTitles } from '../../data/staff.ts'
import { formatLocal } from '../../lib/time.ts'
import { BackLink, Banner, NotAllowed } from '../../ui/components.tsx'
import { Picto } from '../../ui/pictos.tsx'
import { StepUpField, useSensitive } from '../../ui/sensitive.tsx'
import { NoFactorBanner } from './Factor.tsx'

/** "Contacts d'urgence" (M20 decision 11): the emergency contacts of everyone at work at the site today, for the office and for one A4 sheet. Read online after a fresh
 *  second-factor code through the audited `api.read_emergency_contacts` (one audit event: how many people and contacts, never a value) and kept only while the page
 *  is open; its print page is in index.css (the CSP refuses a <style> element).
 *  M23 item 24: contact.manage is the only code on these contacts (the RPC is gated on it); without it the page is the card. Item 32: a missing second factor is said first. */
export function EmergencyPage() {
  const { t, i18n } = useTranslation()
  const { org, site, deviceId, can } = useClub()
  const titles = useJobTitles()
  const s = useSensitive()
  const [sheet, setSheet] = useState<{ data: EmergencySheet; at: Date } | null>(null)
  const load = async () => { const r = await s.call<EmergencySheet>('read_emergency_contacts', { p_org: org.org_id, p_site: site.site_id, p_device_id: deviceId }); if (r) setSheet({ data: r, at: new Date() }) }
  const rows = sheet ? sheetRows(sheet.data, titles ?? [], i18n.language) : []
  const missing = rows.filter((r) => r.missing).length
  if (!can('contact.manage')) return <NotAllowed permission="contact.manage" />
  return (
    <section className="emergency-sheet flex flex-col gap-4" data-testid="emergency-page">
      <div className="flex flex-col gap-4 print:hidden">
        <BackLink to="/people">← {t('common.back')}</BackLink>
        <h1 className="flex items-center gap-2 text-2xl font-bold"><Picto name="phone" className="size-7 text-flag dark:text-red-300" />{t('emergency.title')}</h1>
        <p className="meta">{t('emergency.help')}</p>
        <NoFactorBanner testId="emergency-no-factor" />
        <StepUpField s={s} />
        <div className="flex flex-wrap gap-2">
          <button className="btn btn-primary" disabled={s.busy || !s.online} onClick={() => void load()} data-testid="emergency-show">{t('emergency.show')}</button>
          {sheet && rows.length > 0 && <button className="btn" onClick={() => window.print()} data-testid="emergency-print">{t('emergency.print')}</button>}
        </div>
        {sheet && missing > 0 && <Banner tone="warn"><span data-testid="emergency-missing">{t('emergency.missing', { count: missing })}</span></Banner>}
      </div>
      {sheet && (
        <>
          <header className="hidden border-b-2 border-black pb-2 print:block">
            <p className="text-sm">{org.org_name} · {site.name}</p>
            <h1 className="text-2xl font-bold">{t('emergency.title')}</h1>
          </header>
          {rows.length === 0 ? <p data-testid="emergency-none">{t('emergency.noOne')}</p> : (
            <ul className="flex flex-col gap-2 print:gap-0" data-testid="emergency-list">
              {rows.map((r) => (
                <li key={r.person_id} className="card grid gap-2 break-inside-avoid sm:grid-cols-[minmax(10rem,14rem)_1fr] print:rounded-none print:border-0 print:border-b print:border-black print:p-1 print:shadow-none"
                  data-testid="emergency-person" data-person={r.person_id}>
                  <div>
                    <p className="font-semibold">{r.name}</p>
                    {r.fonction && <p className="meta print:text-black">{r.fonction}</p>}
                  </div>
                  {r.missing ? <p className="text-sm font-semibold text-amber-800 dark:text-amber-300 print:text-black">{t('emergency.none')}</p> : (
                    <ul className="flex flex-col gap-1">
                      {r.contacts.map((c) => (
                        <li key={c.rank} className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                          <span><span className="font-semibold">{c.name}</span> <span className="opacity-75">({c.relationship})</span></span>
                          <a className="btn !px-4 print:!min-h-0 print:border-0 print:!p-0 print:font-bold" href={telHref(c.phone)} aria-label={t('emergency.call', { name: c.name })} data-testid="emergency-tel">{c.phone}</a>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          )}
          <p className="text-sm italic" data-testid="emergency-printed">{t('emergency.printed', { date: formatLocal(sheet.at, site.timezone) })}</p>
        </>
      )}
    </section>
  )
}
